import {
  CurrencyInput,
  Icon,
  LabeledCurrencyInput,
  LabeledNumberInput,
  LabeledRadioInput,
  LabeledTextInput,
  LabeledToggleInput,
  ModalButton,
  SyncHeight,
  SyncMultipleHeightProps,
  TooltipWrap
} from "adviesbox-shared";
import { LocalDate } from "@js-joda/core";
import { connect, FormikContextType, FormikHelpers } from "formik";
import React, { ReactElement, useCallback } from "react";
import { Button } from "react-bootstrap";
import { bindSaveFunction } from "../../shared/utils/bind-save-function";
import { enum2options } from "../../shared/utils/enum-to-options";
import { getSum } from "../../shared/utils/helpers";
import { getInkomenEnFiscusTextResources } from "../infra/inkomen-en-fiscus-resources";
import {
  InkomenEnFiscusState,
  InkomenSyncHeightIndex,
  InkomenVerklaring,
  OverigeInkomstenModalType
} from "../infra/inkomen-en-fiscus-schema";
import InkomenUitArbeidModal from "../inkomen-uit-arbeid-modal/inkomen-uit-arbeid-modal";
import InkomenUitArbeidScenario from "../inkomen-uit-arbeid-modal/inkomen-uit-arbeid-scenario";
import InkomstenAanmerkelijkBelangModal from "../inkomsten-aanmerkelijk-belang-modal/inkomsten-aanmerkelijk-belang-modal";
import InkomstenAanmerkelijkBelangScenario from "../inkomsten-aanmerkelijk-belang-modal/inkomsten-aanmerkelijk-belang-scenario";
import InkomstenUitBeleggingenModal from "../inkomsten-uit-beleggingen-modal/inkomsten-uit-beleggingen-modal";
import InkomstenVerhuurOnroerendGoedModal from "../inkomsten-verhuur-onroerend-goed-modal/inkomsten-verhuur-onroerend-goed-modal";
import OndernemingModal from "../onderneming-modal/onderneming-modal";
import OndernemingScenario from "../onderneming-modal/onderneming-scenario";
import OverigeInkomstenModal from "../overige-inkomsten-modal/overige-inkomsten-modal";
import OverigeWerkzaamhedenModal from "../overige-werkzaamheden-modal/overige-werkzaamheden-modal";
import OverigeWerkzaamhedenScenario from "../overige-werkzaamheden-modal/overige-werkzaamheden-scenario";

export const aanvrager1CalcOverigeInkomstenBox1Totaal = (
  result: OverigeInkomstenModalType,
  setFieldValue: FormikHelpers<InkomenEnFiscusState>["setFieldValue"]
): void => {
  setFieldValue(
    "aanvrager1Inkomen.overigeInkomstenBox1.totaal",
    getSum([
      result.alimentatieExBedrag,
      result.anwUitkeringBedrag,
      result.ivaUitkeringBedrag,
      result.calculatedDate?.isAfter(LocalDate.now())
        ? result.nabestaandenpensioenVoorAowBedrag
        : result.nabestaandenpensioenNaAowBedrag,
      result.overigeInkomsten1Bedrag,
      result.overigeInkomsten2Bedrag,
      result.rwwUitkeringBedrag,
      result.wachtgeldBedrag,
      result.wajongUitkeringBedrag,
      result.waoUitkeringBlijvendBedrag,
      result.waoUitkeringTijdelijkBedrag,
      result.wazUitkeringBedrag,
      result.wgaUitkeringBedrag,
      result.wwUitkeringBedrag
    ])
  );
};

const Aanvrager1Inkomen = ({
  formik: {
    setFieldValue,
    values: { aanvrager1Inkomen, aanvrager1Aow }
  },
  targetRef,
  sourceRef
}: SyncMultipleHeightProps & {
  formik: FormikContextType<InkomenEnFiscusState>;
}): ReactElement => {
  const createSaveFunction = useCallback(bindSaveFunction(setFieldValue), [setFieldValue]);
  const firstInkomenUitArbeid = aanvrager1Inkomen.inkomenUitArbeid[0];
  const isCalculating =
    aanvrager1Inkomen.inkomenUitArbeid.length === 1 &&
    (aanvrager1Inkomen.brutoSalarisUitDienstverband || 0) !== (firstInkomenUitArbeid.totaalBruto || 0);

  return (
    <>
      <SyncHeight
        sourceRef={sourceRef && sourceRef[InkomenSyncHeightIndex.BrutoSalarisUitDienstverband]}
        targetRef={targetRef && targetRef[InkomenSyncHeightIndex.BrutoSalarisUitDienstverband]}
      >
        <LabeledToggleInput
          caption="Bruto salaris uit dienstverband"
          name="aanvrager1Inkomen.brutoSalarisUitDienstverbandIsEnabled"
          enableWarning={
            aanvrager1Inkomen.inkomenUitArbeid.length > 1 ||
            aanvrager1Inkomen.brutoSalarisUitDienstverband !== null ||
            !!aanvrager1Inkomen.inkomenUitArbeid[0].totaalBruto
          }
          warningTitle={getInkomenEnFiscusTextResources("WarningTitleInkomenUitArbeidVerwijderen")}
          warningTekst={getInkomenEnFiscusTextResources("WarningTekstInkomenUitArbeidVerwijderen")}
          warningConfirmButtonTekst={getInkomenEnFiscusTextResources("WarningConfirmButtonkomenUitArbeidVerwijderen")}
          appendChildren={
            <CurrencyInput
              name={"aanvrager1Inkomen.brutoSalarisUitDienstverband"}
              readonly={aanvrager1Inkomen.inkomenUitArbeid.length > 1}
              placeholder="-"
              appendChildren={
                <>
                  <ModalButton
                    parent="aanvrager1Inkomen.inkomenUitArbeid"
                    resetSize={true}
                    content={<Icon name="specificatie" alt="Inkomen uit arbeid" />}
                    size="lg"
                    disabled={isCalculating}
                  >
                    <InkomenUitArbeidModal
                      geboorteDatum={aanvrager1Aow.geboorteDatum}
                      data={aanvrager1Inkomen.inkomenUitArbeid}
                      onSave={createSaveFunction("aanvrager1Inkomen.inkomenUitArbeid")}
                    />
                  </ModalButton>
                  <ModalButton
                    distinguisher="modal-scenario"
                    parent="aanvrager1Inkomen.inkomenUitArbeid"
                    resetSize={true}
                    content={<Icon name="scenario" alt="Scenario inkomen" />}
                    disabled={isCalculating}
                  >
                    <InkomenUitArbeidScenario
                      geboorteDatum={aanvrager1Aow.geboorteDatum}
                      data={aanvrager1Inkomen.inkomenUitArbeid}
                      onSave={createSaveFunction("aanvrager1Inkomen.inkomenUitArbeid")}
                    />
                  </ModalButton>
                </>
              }
            />
          }
        />
        {aanvrager1Inkomen.brutoSalarisUitDienstverbandIsEnabled && (
          <div>
            <LabeledRadioInput
              caption="Verklaring inkomen"
              name="aanvrager1Inkomen.verklaringInkomen"
              options={enum2options(InkomenVerklaring)}
            />
          </div>
        )}
        {aanvrager1Inkomen.verklaringInkomen === InkomenVerklaring.ArbeidScan && (
          <>
            <LabeledCurrencyInput caption="Verdiencapaciteit" name="aanvrager1Inkomen.arbeidsscanVerdiencapaciteit" />
            <LabeledNumberInput caption="Score" name="aanvrager1Inkomen.arbeidsscanScore" fieldSize="l" />
            <LabeledTextInput caption="Rapportnummer" name="aanvrager1Inkomen.arbeidsscanRapportnummer" />
          </>
        )}

        {aanvrager1Inkomen.verklaringInkomen === InkomenVerklaring.UWVdocument && (
          <div className="form-group form-row mt-2">
            <div className="col-5">
              <a
                href={"https://www.toetsinkomenberekenen.nl/"}
                className="link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="pl-3 pr-3">
                  <span>Toetsinkomen UWV</span>&nbsp;
                  <i className="icon externallink" />
                </span>
              </a>
              <TooltipWrap
                name="aanvrager1Inkomen.toetsinkomenUwv"
                warningText={getInkomenEnFiscusTextResources("TooltipToetsinkomenUwv")}
                placement="bottom"
                tooltipClasses="question-tip"
                iconType="questionmark"
              >
                <Button className="question-tip" id="aanvrager1Inkomen.toetsinkomenUwv-tooltip" />
              </TooltipWrap>
            </div>
            <div className="col-7">
              <CurrencyInput name={`aanvrager1Inkomen.toetsinkomenUwv`} decimalen={0} />
            </div>
          </div>
        )}
      </SyncHeight>

      <LabeledToggleInput
        caption="Onderneming"
        name="aanvrager1Inkomen.ondernemingIsEnabled"
        appendChildren={
          <CurrencyInput
            name="aanvrager1Inkomen.onderneming.inkomensverleden.gemiddeld"
            readonly={true}
            placeholder="-"
            appendChildren={
              <>
                <ModalButton
                  resetSize={true}
                  parent="aanvrager1Inkomen.onderneming"
                  content={<Icon name="specificatie" alt="Onderneming" />}
                >
                  <OndernemingModal
                    data={aanvrager1Inkomen.onderneming}
                    onSave={createSaveFunction("aanvrager1Inkomen.onderneming")}
                  />
                </ModalButton>
                <ModalButton
                  resetSize={true}
                  parent="aanvrager1Inkomen.onderneming.scenario"
                  content={<Icon name="scenario" alt="Scenario onderneming" />}
                  size="lg"
                >
                  <OndernemingScenario
                    geboorteDatum={aanvrager1Aow.geboorteDatum}
                    data={aanvrager1Inkomen.onderneming}
                    onSave={createSaveFunction("aanvrager1Inkomen.onderneming")}
                  />
                </ModalButton>
              </>
            }
          />
        }
      />

      <LabeledToggleInput
        caption="Overige werkzaamheden"
        name="aanvrager1Inkomen.overigeWerkzaamhedenIsEnabled"
        appendChildren={
          <CurrencyInput
            name="aanvrager1Inkomen.overigeWerkzaamheden.inkomensverleden.gemiddeld"
            readonly={true}
            placeholder="-"
            appendChildren={
              <>
                <ModalButton
                  resetSize={true}
                  parent="aanvrager1Inkomen.overigeWerkzaamheden"
                  content={<Icon name="specificatie" alt="Overige werkzaamheden" />}
                >
                  <OverigeWerkzaamhedenModal
                    data={aanvrager1Inkomen.overigeWerkzaamheden}
                    onSave={createSaveFunction("aanvrager1Inkomen.overigeWerkzaamheden")}
                  />
                </ModalButton>
                <ModalButton
                  resetSize={true}
                  parent="aanvrager1Inkomen.overigeWerkzaamheden.scenario"
                  content={<Icon name="scenario" alt="Scenario overige werkzaamheden" />}
                  size="lg"
                >
                  <OverigeWerkzaamhedenScenario
                    geboorteDatum={aanvrager1Aow.geboorteDatum}
                    data={aanvrager1Inkomen.overigeWerkzaamheden}
                    onSave={createSaveFunction("aanvrager1Inkomen.overigeWerkzaamheden")}
                  />
                </ModalButton>
              </>
            }
          />
        }
      />

      <LabeledToggleInput
        caption="Overige inkomsten box 1"
        name="aanvrager1Inkomen.overigeInkomstenBox1IsEnabled"
        appendChildren={
          <CurrencyInput
            name="aanvrager1Inkomen.overigeInkomstenBox1.totaal"
            placeholder="-"
            readonly={true}
            appendChildren={
              <ModalButton
                resetSize={true}
                parent="aanvrager1Inkomen.overigeInkomstenBox1"
                content={<Icon name="specificatie" alt="Overige inkomsten" />}
                size="lg"
              >
                <OverigeInkomstenModal
                  data={aanvrager1Inkomen.overigeInkomstenBox1}
                  onSave={createSaveFunction(
                    "aanvrager1Inkomen.overigeInkomstenBox1",
                    aanvrager1CalcOverigeInkomstenBox1Totaal
                  )}
                />
              </ModalButton>
            }
          />
        }
      />

      <LabeledToggleInput
        caption="Inkomsten aanmerkelijk belang"
        name="aanvrager1Inkomen.inkomstenAanmerkelijkBelangIsEnabled"
        appendChildren={
          <CurrencyInput
            name="aanvrager1Inkomen.inkomstenAanmerkelijkBelang.inkomensverleden.gemiddeld"
            readonly={true}
            placeholder="-"
            appendChildren={
              <>
                <ModalButton
                  resetSize={true}
                  parent="aanvrager1Inkomen.inkomstenAanmerkelijkBelang"
                  content={<Icon name="specificatie" alt="Inkomsten aanmerkelijk belang" />}
                >
                  <InkomstenAanmerkelijkBelangModal
                    data={aanvrager1Inkomen.inkomstenAanmerkelijkBelang}
                    onSave={createSaveFunction("aanvrager1Inkomen.inkomstenAanmerkelijkBelang")}
                  />
                </ModalButton>
                <ModalButton
                  resetSize={true}
                  parent="aanvrager1Inkomen.inkomstenAanmerkelijkBelang.scenario"
                  content={<Icon name="scenario" alt="Scenario inkomsten aanmerkelijk belang" />}
                  size="lg"
                >
                  <InkomstenAanmerkelijkBelangScenario
                    geboorteDatum={aanvrager1Aow.geboorteDatum}
                    data={aanvrager1Inkomen.inkomstenAanmerkelijkBelang}
                    onSave={createSaveFunction("aanvrager1Inkomen.inkomstenAanmerkelijkBelang")}
                  />
                </ModalButton>
              </>
            }
          />
        }
      />

      <SyncHeight
        sourceRef={sourceRef && sourceRef[InkomenSyncHeightIndex.InkomstenBox3]}
        targetRef={targetRef && targetRef[InkomenSyncHeightIndex.InkomstenBox3]}
      >
        <LabeledToggleInput caption="Inkomsten box 3" name="aanvrager1Inkomen.inkomstenBox3IsEnabled" />

        {aanvrager1Inkomen.inkomstenBox3IsEnabled && (
          <>
            <div>
              <LabeledCurrencyInput
                caption="Inkomsten verhuur onr. goed"
                name="aanvrager1Inkomen.inkomstenVerhuurOnroerendGoed.inkomstenVerhuurOnroerendGoed"
                readonly={true}
                placeholder="-"
                appendChildren={
                  <ModalButton
                    resetSize={true}
                    parent="aanvrager1Inkomen.inkomstenVerhuurOnroerendGoed"
                    content={<Icon name="specificatie" alt="Inkomsten verhuur onr. goed" />}
                  >
                    <InkomstenVerhuurOnroerendGoedModal
                      data={aanvrager1Inkomen.inkomstenVerhuurOnroerendGoed}
                      onSave={createSaveFunction("aanvrager1Inkomen.inkomstenVerhuurOnroerendGoed")}
                    />
                  </ModalButton>
                }
              />
            </div>
            <div>
              <LabeledCurrencyInput
                caption="Inkomsten uit beleggingen"
                name="aanvrager1Inkomen.inkomstenUitBeleggingen.inkomstenUitBeleggingen"
                readonly={true}
                placeholder="-"
                appendChildren={
                  <ModalButton
                    resetSize={true}
                    parent="aanvrager1Inkomen.inkomstenUitBeleggingen"
                    content={<Icon name="specificatie" alt="Inkomsten uit beleggingen" />}
                  >
                    <InkomstenUitBeleggingenModal
                      data={aanvrager1Inkomen.inkomstenUitBeleggingen}
                      onSave={createSaveFunction("aanvrager1Inkomen.inkomstenUitBeleggingen")}
                    />
                  </ModalButton>
                }
              />
            </div>
          </>
        )}
      </SyncHeight>
    </>
  );
};

export default connect<SyncMultipleHeightProps, InkomenEnFiscusState>(Aanvrager1Inkomen);
